import _                                                      from 'underscore';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { Cell, Grid }                                         from "styled-css-grid";
import { Button, Form, Icon }                                 from 'semantic-ui-react';
import { Formik, FormikConfig }                               from 'formik';
import { QueryHookOptions, useMutation, useQuery }            from '@apollo/react-hooks';
import * as Yup                                               from 'yup';

import { FlexAlign, FlexJustify, Uuid }                                                                from '../../../../types';
import { runIfFunction }                                                                               from '../../../../util/FunctionalUtility';
import { DecisionEngineQueries, SystemQueries }                                                        from '../../../../queries';
import { ProjectActivityStatusChangeCallback, useAuditRecords, useProjectActivities, useSystemToasts } from '../../../../hooks';
import { queriesHaveData, queriesLoading }                                                             from '../../../../store/_apolloUtil';
import { ACTIVITY_STATUS, ACTIVITY_STATUS_INFO }                                                       from '../../../../constants/ProductEnums';
import { AUDIT_RECORD_TYPE }                                                                           from '../../../../constants/SystemRecordEnums';
import { Flexbox, FlexItem }                                                                           from '../../../containers';
import { CompletionStatusDropdown }                                                                    from '../../../components/forms/dropdowns/CompletionStatusDropdown';
import { AuditMemoList }                                                                               from '../../../components/audits/AuditMemoList';
import { LastUpdatedAuditTag }                                                                         from '../../../components/audits/LastUpdatedAuditTag';
import { ProjectContext }                                                                              from '../ProjectIndexPage';
import { IncompleteParentActivitiesWarning }                                                           from './ProjectActivityParentProgressDetails';
import { InlineLoadingIndicatorLabel }                                                                 from '../../../components/util/loading/InlineLoadingIndicatorLabel';
import { maybeStaticVersionField }                                                                     from "@src/util";
import { ProjectQueries }                                                                              from "@src/queries/ProjectQueries";
import { InteropService }                                                                              from "@src/services";


interface Props {
    id: Uuid,
    projectId?: Uuid,
    projectVersionId?: Uuid,
    editable?: boolean,
    organizationId?: Uuid,
    withWarning?: boolean,
    withLastUpdated?: boolean,
    omitExcluded?: boolean,
    onStatusChange?: ProjectActivityStatusChangeCallback
}

type ProjectActivityStatusUpdateControlFormValues = FormikConfig<{
    status: ACTIVITY_STATUS,
}>;

const defaultProps: Props = {
    id:               null,
    projectId:        null,
    projectVersionId: null,
    editable:         true,
    organizationId:   null,
    withWarning:      true,
    withLastUpdated:  true,
    omitExcluded:     false,
    onStatusChange:   null,
}

const ProjectActivityStatusUpdateControl: FC<Props> = ( props ) => {

    let projectContext                          = useContext( ProjectContext ),
        projectId                               = projectContext.projectId ?? props.projectId ?? null,
        projectVersionId                        = projectContext.projectVersionId ?? props.projectVersionId ?? null,
        organizationId                          = projectContext.organizationId ?? props.organizationId ?? null,
        toastActions                            = useSystemToasts(),
        { getAuditRecordTypes }                 = useAuditRecords(),
        { getUpdateProjectActivityInputData }   = useProjectActivities(),
        [ projectActivity, setProjectActivity ] = useState( null ),
        [ isProcessing, setIsProcessing ]       = useState( false ),
        formikRef                               = useRef();

    /* Get the status */
    let getProjectActivityStatusQuery = DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `
			${ projectVersionId
               ? DecisionEngineQueries.fragments.STATIC_PROJECT_ACTIVITY.USER_EDIT_FIELDS_VITAL( projectVersionId )
               : DecisionEngineQueries.fragments.PROJECT_ACTIVITY.USER_EDIT_FIELDS_VITAL }
			${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
		`, projectVersionId ),
        getProjectActivityStatusVars  = {
            variables:   {
                projectId,
                projectVersionId,
                where: {
                    id: {
                        eq: props.id
                    }
                },
            },
            fetchPolicy: 'network-only'
        } as QueryHookOptions,
        getProjectActivityStatusReq   = useQuery(
            getProjectActivityStatusQuery,
            getProjectActivityStatusVars
        );

    /* Mutation to update the status */
    let [ updateProjectActivityStatus ] = useMutation(
        projectVersionId
        ? ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA()
        : DecisionEngineQueries.mutations.UPDATE_PROJECT_ACTIVITIES()
    );

    /* Initial loading */
    let isLoading = queriesLoading( [ getProjectActivityStatusReq ] ) || _.isNull( projectActivity );

    /* When state info arrives...*/
    useEffect( () => {

        if( !queriesLoading( getProjectActivityStatusReq ) &&
            queriesHaveData( getProjectActivityStatusReq ) ){

            const projectActivitiesField = maybeStaticVersionField( 'projectActivites', projectVersionId );

            // pluck the activity off
            let projectActivity = _.first( getProjectActivityStatusReq.data[ projectActivitiesField ] ) ?? null;

            setProjectActivity( projectActivity );
        }

    }, [ getProjectActivityStatusReq ] );

    /* When status update request */
    const handleUpdateActivityStatus: ProjectActivityStatusUpdateControlFormValues['onSubmit'] = async ( values, formikHelpers ) => {

        setIsProcessing( true );

        let oldValue                = projectActivity.completionState,
            newValue                = formikRef.current.values.status;

        // craft a single delta object
        let deltaObject = [
            {
                projectActivityId: props.id,
                oldValue,
                newValue
            }
        ];

        // other required PA data
        let updateData = getUpdateProjectActivityInputData( projectActivity )

        try{
            let response = await updateProjectActivityStatus( {
                variables:           {
                    input: projectVersionId ? {
                        projectVersionId,
                        activityChanges: {
                            modified: [ {
                                id:          props.id,
                                percentDone: InteropService.getActivityStatusToPercentDone( newValue ),
                            } ]
                        }
                    } : {
                        projectId:         projectContext.projectId,
                        projectActivities: [
                            {
                                ...updateData,
                                completionState: newValue,
                            }
                        ]
                    }
                },
                awaitRefetchQueries: true,
                refetchQueries:      [
                    { query: getProjectActivityStatusQuery, ...getProjectActivityStatusVars }
                ],
            } );

            setIsProcessing( false );

            if( queriesHaveData( response ) ){

                toastActions.success( 'Activity Status Updated' );

                runIfFunction( props.onStatusChange, deltaObject );

            } else {

                formikRef.current.resetForm();

                toastActions.error( 'Update Failed', 'An issue prevented Corval from updating this activity.' );
            }

        } catch( e ) {

            setIsProcessing( false );

            toastActions.error( 'Network Error', `${ e.message }` );
        }
    }

    const getMappedRequestData = () => {

        return {
            status: ( !_.isNull( projectActivity ) ) ? projectActivity.completionState : ACTIVITY_STATUS.NOTSTARTED
        }
    }

    /* Formik */
    let formProps: ProjectActivityStatusUpdateControlFormValues = {
        innerRef:           formikRef,
        isInitialValid:     true,
        enableReinitialize: true,
        initialValues:      getMappedRequestData(),
        validationSchema:   Yup.object( { status: Yup.string() } ),
        onSubmit:           handleUpdateActivityStatus,
    }

    return (

        <Formik { ...formProps }>

            { formik => {

                let statusInfo      = isLoading ? null : ACTIVITY_STATUS_INFO[ formik.values.status ],
                    hasAuditEntries = isLoading ? false : getAuditRecordTypes( projectActivity.auditEntries, [ AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_STATUS_CHANGED ], 'newest', [ props.id ], ),
                    isComplete      = ( formik.values.status === ACTIVITY_STATUS.COMPLETE ),
                    isEditable      = props.editable;

                return (
                    <>
                        { isLoading
                          ? ( <InlineLoadingIndicatorLabel label={ 'Getting Status...' }/> )
                          : ( <Form>

                                    <Grid columns={ `30px minmax(150px, auto) 70px` } rowGap={ '5px' } rows={ `auto auto auto` } className={ 'padding-xs-top' }>

                                        <Cell middle center>

                                            <Icon fitted name={ statusInfo.ICON } color={ statusInfo.COLOR } size={ 'large' }/>

                                        </Cell>
                                        <Cell middle>

                                            <CompletionStatusDropdown
                                                name={ 'status' }
                                                disabled={ !isEditable || isProcessing }
                                                omit={ ( props.omitExcluded || projectActivity.activity.isRequired ) ? [ ACTIVITY_STATUS.EXCLUDED ] : [] }
                                            />

                                        </Cell>
                                        <Cell className={ 'text-right' }>

                                            <Button
                                                type={ 'button' }
                                                size={ 'mini' }
                                                content={ 'Update' }
                                                disabled={ !formik.dirty }
                                                primary={ formik.dirty }
                                                onClick={ formik.submitForm }
                                                loading={ isProcessing }
                                                style={ { height: '100%' } }
                                            />

                                        </Cell>

                                        <Cell top={ 2 } width={ 1 } left={ 2 }>

                                            <Flexbox justify={ FlexJustify.START } align={ FlexAlign.CENTER }>

                                                {/* Auditing entry */ }
                                                { hasAuditEntries && props.withLastUpdated && (

                                                    <AuditMemoList
                                                        organizationId={ organizationId }
                                                        records={ projectActivity.auditEntries }
                                                        types={ [ AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_STATUS_CHANGED ] }
                                                        timeBoundary={ 'newest' }
                                                        entityIds={ [ props.id ] }
                                                        actionDesc={ '' }
                                                        actionVerb={ isComplete ? 'completed' : 'updated' }
                                                        auditSubject={ isComplete ? 'this activity' : `this activity's status` }
                                                        wrapperComponent={ ( props ) => (
                                                            <FlexItem justify={ FlexJustify.START } noGrow className={ 'padding-xs-right' }>
                                                                <LastUpdatedAuditTag { ...props } useAvatar={ true } usePopup={ false }/>
                                                            </FlexItem>
                                                        ) }
                                                    />
                                                ) }

                                                {/* Parent Activity information */ }
                                                { isComplete && props.withWarning && (
                                                    <FlexItem justify={ FlexJustify.START } noGrow>
                                                        <IncompleteParentActivitiesWarning
                                                            projectId={ projectId }
                                                            projectActivityId={ props.id }
                                                        />
                                                    </FlexItem>
                                                ) }
                                            </Flexbox>
                                        </Cell>
                                    </Grid>
                                </Form>
                          ) }
                    </>
                )
            } }
        </Formik>
    );
};

ProjectActivityStatusUpdateControl.defaultProps = defaultProps;

export { ProjectActivityStatusUpdateControl }
